import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import { PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ProductManagement/constants'
import { ProductDetails } from 'Services/ProductManagement/Features/Product/Components/ProductDetails'
import ProductPricing from 'Services/ProductManagement/Features/Product/Components/ProductPricing'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import { useGetSingleProductQuery } from 'Services/ProductManagement/Features/Product/productApi'
import { PricingTabType } from 'Services/ProductManagement/Features/Product/types'
import { useSelectedTariff } from 'Services/ProductManagement/Features/Tariff/Hooks'
import { TariffCatalogProductTabType } from 'Services/ProductManagement/Features/Tariff/types'
import { useGetSingleVatDefinitionQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import { GridHighlightProvider } from 'Util/Context/gridHighlightContext'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import {
  IS_TARIFF_EDIT_ALLOWED,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PRODUCT_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface TariffCatalogProductDrawerProps {
  open: boolean
  handleClose: () => void
  handleProductDelete: (productId: string) => void
}

export function TariffCatalogProductDrawer({
  open,
  handleClose,
  handleProductDelete
}: Readonly<TariffCatalogProductDrawerProps>) {
  const { id: tariffId, catalogId } = useParams()
  const { confirm } = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const { singleTariff } = useSelectedTariff(tariffId)
  const { confirmForUnsavedChanges } = useUnsavedChanges()
  const navigate = useNavigate()
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`

  const { data: vatDefinition = null, isLoading: isVatDefinitionsLoading } =
    useGetSingleVatDefinitionQuery(singleTariff?.vat_definition_id ?? '', {
      skip: !open || !singleTariff?.vat_definition_id
    })

  const productDetailsDrawerTabFromParams =
    searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) ?? TariffCatalogProductTabType.DETAILS

  const selectedProductId = searchParams.get(SELECTED_PRODUCT_PARAMETER_KEY)
  const { data: singleProduct, isFetching: isSingleProductFetching } = useGetSingleProductQuery(
    selectedProductId ?? '',
    {
      skip: !selectedProductId
    }
  )

  const handleProductDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: TariffCatalogProductTabType
  ) => {
    confirmForUnsavedChanges(() => {
      searchParams.set(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
      setSearchParams(searchParams)
    })
  }

  const handleDrawerClose = () => {
    confirmForUnsavedChanges(() => {
      searchParams.delete(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY)
      setSearchParams(searchParams)
      handleClose()
    })
  }

  /**
   * In Waltti environment only Exchange period is shown on tariff product level.
   * HSL environment has only Pricing enabled.
   */
  const allowedPricingTabs = [PricingTabType.PRICINGS]

  const showProductDeleteButton =
    singleProduct && IS_TARIFF_EDIT_ALLOWED && IS_WALTTI_SERVICE_ENVIRONMENT
  const showProductEditButton = singleProduct && IS_TARIFF_EDIT_ALLOWED

  return (
    <DrawerRight
      open={open}
      onClose={handleDrawerClose}
      topBar={
        <Box>
          {showProductDeleteButton && (
            <DeleteButton
              sx={{ mr: 1 }}
              size="small"
              onClick={() =>
                confirm({
                  title: t`Are you sure?`,
                  message: t`Are you sure you want to delete the product?`,
                  handleConfirm: () => {
                    handleProductDelete(singleProduct.id)
                  }
                })
              }
            >{t`Delete`}</DeleteButton>
          )}
          {showProductEditButton && (
            <CustomButton
              onClick={() =>
                navigate(
                  `${baseUrl}/${tariffId}/sales-channels/${catalogId}/products/${singleProduct.id}/edit`
                )
              }
              size="small"
              startIcon={<EditIcon />}
            >{t`Edit`}</CustomButton>
          )}
        </Box>
      }
    >
      <TabContext value={productDetailsDrawerTabFromParams}>
        <TabList
          onChange={handleProductDetailsDrawerTabChange}
          aria-label={t`Sales channel product tab list`}
        >
          <Tab label={t`Product details`} value={TariffCatalogProductTabType.DETAILS} />
          <Tab label={t`Pricing`} value={TariffCatalogProductTabType.PRICING} />
        </TabList>
        <Divider />
        <TabPanel sx={{ p: 0 }} value={TariffCatalogProductTabType.DETAILS}>
          <WithLoader loading={isSingleProductFetching}>
            {singleProduct && (
              <ProductDetails
                product={singleProduct}
                vatDefinition={vatDefinition}
                isVatDefinitionsLoading={isVatDefinitionsLoading}
              />
            )}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffCatalogProductTabType.PRICING}>
          <WithLoader loading={isSingleProductFetching}>
            <GridHighlightProvider>
              {singleProduct && (
                <ProductPricing
                  key={singleProduct.id}
                  product={singleProduct}
                  allowedTabs={allowedPricingTabs}
                />
              )}
            </GridHighlightProvider>
          </WithLoader>
        </TabPanel>
      </TabContext>
    </DrawerRight>
  )
}

export default TariffCatalogProductDrawer
